import React, { useState } from "react";
import { Link } from "react-router-dom";
import { addDays } from "date-fns";
import { Bell, Mail, FileUp, X, FileText, Lock, Info } from "lucide-react";
import { Field, StatusPill } from "@/components/common";
import { countdownLabel, fmtBytes, isoOf, toDate, RECURRENCE_LABELS, fmtDate } from "@/lib/brand";
import { nextOccurrence, daysLeft } from "@/lib/dates";

export const PRESET_OFFSETS = [365, 180, 90, 30, 14, 7, 1, 0];
export const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

export function CountdownPreview({ form, today, fieldSet }) {
  if (!form.event_date) {
    return <div className="text-center py-10 text-sm text-[var(--pm-muted)]" data-testid="countdown-preview-empty">Pick a date to see the countdown.</div>;
  }
  const nd = nextOccurrence(form.event_date, form.recurrence, today, form.leap_day_policy);
  const d = daysLeft(form.event_date, form.recurrence, today, form.leap_day_policy);
  return (
    <div className="text-center" data-testid="countdown-preview">
      <p className="text-xs font-semibold text-[var(--pm-muted)]">Countdown Preview</p>
      <p className={`text-7xl font-extrabold tracking-tight mt-2 ${d < 0 ? "text-[var(--pm-danger)]" : "text-[var(--pm-primary)]"}`} data-testid="countdown-preview-number">{Math.abs(d)}</p>
      <p className="font-bold">{d === 0 ? "Due today" : d > 0 ? `day${d === 1 ? "" : "s"} left` : `day${d === -1 ? "" : "s"} overdue`}</p>
      <p className="text-xs text-[var(--pm-muted)] mt-1">{fmtDate(nd)}{form.recurrence !== "one_time" ? ` · ${RECURRENCE_LABELS[form.recurrence]}` : ""}</p>
      {fieldSet && <div className="border-t border-[var(--pm-border)] mt-6 pt-5">
        <p className="text-xs text-[var(--pm-muted)]">Suggested reminders</p>
        <span className="pm-chip bg-[#EFEBFF] text-[var(--pm-primary)] mt-2">{fieldSet.offsets.join(" · ")} days</span>
      </div>}
    </div>
  );
}

export function DetailsStep({ form, set, setCustom, meta, entities, errors, onCategory, onSub }) {
  const cat = meta.categories.find((c) => c.id === form.category_id);
  const sub = cat?.subcategories.find((s) => s.id === form.subcategory_id);
  const fs = meta.field_sets[sub?.field_set || "generic"];
  const isFeb29 = form.event_date?.slice(5) === "02-29";
  return (
    <div className="grid sm:grid-cols-2 gap-5" data-testid="details-step">
      <Field label="Reminder category" htmlFor="f-cat">
        <select id="f-cat" className="pm-input" value={form.category_id} onChange={(e) => onCategory(e.target.value)} data-testid="form-category-select">
          {meta.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Subcategory" htmlFor="f-sub">
        <select id="f-sub" className="pm-input" value={form.subcategory_id} onChange={(e) => onSub(e.target.value)} data-testid="form-subcategory-select">
          {cat?.subcategories.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </Field>
      <div className="sm:col-span-2"><Field label="Reminder title" required error={errors.title} htmlFor="f-title">
        <input id="f-title" className="pm-input" value={form.title} onChange={set("title")} placeholder={`e.g. My ${sub?.name || "reminder"}`} aria-invalid={!!errors.title} data-testid="form-title-input" />
      </Field></div>
      <div className="sm:col-span-2"><Field label="Assign to" hint={<>Link to a person or asset. <Link to="/people-assets" className="text-[var(--pm-primary)] font-semibold">Manage people & assets</Link></>} htmlFor="f-owner">
        <select id="f-owner" className="pm-input" value={form.owner_id || ""} onChange={set("owner_id")} data-testid="form-owner-select">
          <option value="">No owner</option>{entities.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.kind})</option>)}
        </select>
      </Field></div>
      {fs.fields.map((k) => {
        const def = meta.field_defs[k];
        const v = form.custom_fields[k] ?? "";
        if (def.type === "boolean") return (
          <label key={k} className="flex items-center gap-3 text-sm font-semibold self-end pb-3">
            <input type="checkbox" className="w-4 h-4 accent-[var(--pm-primary)]" checked={!!v} onChange={(e) => setCustom(k, e.target.checked)} data-testid={`form-custom-${k}`} />{def.label}
          </label>);
        return (
          <Field key={k} label={def.label} hint={def.sensitive ? "Stored privately and masked in lists & exports" : null} htmlFor={`f-${k}`}>
            <input id={`f-${k}`} type={def.type === "date" ? "date" : "text"} className="pm-input" value={v} onChange={(e) => setCustom(k, e.target.value)} data-testid={`form-custom-${k}`} />
          </Field>);
      })}
      <Field label={fs.date_label} required error={errors.event_date} htmlFor="f-date">
        <input id="f-date" type="date" className="pm-input" value={form.event_date} onChange={set("event_date")} aria-invalid={!!errors.event_date} data-testid="form-date-input" />
      </Field>
      <Field label="Repeats" htmlFor="f-rec" hint={fs.renewable && form.recurrence === "one_time" ? "Expiries don't repeat — use Renew to record a new date." : null}>
        <select id="f-rec" className="pm-input" value={form.recurrence} onChange={set("recurrence")} data-testid="form-recurrence-select">
          {Object.entries(RECURRENCE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </Field>
      <Field label="Type of date" htmlFor="f-kind">
        <select id="f-kind" className="pm-input" value={form.date_kind} onChange={set("date_kind")} data-testid="form-datekind-select">
          <option value="all_day">All-day date</option><option value="instant">Appointment with a time</option>
        </select>
      </Field>
      {form.date_kind === "instant" ? (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Time" htmlFor="f-time"><input id="f-time" type="time" className="pm-input" value={form.event_time || ""} onChange={set("event_time")} data-testid="form-time-input" /></Field>
          <Field label="Time zone" htmlFor="f-tz"><input id="f-tz" className="pm-input" value={form.time_zone || ""} onChange={set("time_zone")} placeholder="Asia/Dubai" data-testid="form-timezone-input" /></Field>
        </div>
      ) : <div className="hidden sm:block" />}
      {(isFeb29 && form.recurrence === "yearly") && (
        <div className="sm:col-span-2"><Field label="In non-leap years, celebrate on" htmlFor="f-leap">
          <select id="f-leap" className="pm-input" value={form.leap_day_policy} onChange={set("leap_day_policy")} data-testid="form-leap-select">
            <option value="feb28">February 28</option><option value="mar1">March 1</option>
          </select></Field></div>
      )}
      {fs.cost && (
        <div className="grid grid-cols-[1fr_110px] gap-3">
          <Field label="Estimated cost (optional)" error={errors.cost_amount} htmlFor="f-cost"><input id="f-cost" type="number" min="0" step="0.01" className="pm-input" value={form.cost_amount ?? ""} onChange={set("cost_amount")} data-testid="form-cost-input" /></Field>
          <Field label="Currency" error={errors.cost_currency} htmlFor="f-cur"><input id="f-cur" maxLength={3} className="pm-input uppercase" value={form.cost_currency || ""} onChange={set("cost_currency")} placeholder="AED" data-testid="form-currency-input" /></Field>
        </div>
      )}
      <Field label="Tags" hint="Separate with commas" htmlFor="f-tags"><input id="f-tags" className="pm-input" value={form.tagsText} onChange={set("tagsText")} placeholder="family, travel" data-testid="form-tags-input" /></Field>
      <div className="sm:col-span-2"><Field label="Notes" htmlFor="f-notes"><textarea id="f-notes" className="pm-input" rows={3} value={form.notes} onChange={set("notes")} placeholder="Keep a digital copy and allow time for renewal" data-testid="form-notes-input" /></Field></div>
      <label className="flex items-center gap-3 text-sm font-semibold sm:col-span-2">
        <input type="checkbox" className="w-4 h-4 accent-[var(--pm-primary)]" checked={form.favorite} onChange={(e) => set("favorite")({ target: { value: e.target.checked } })} data-testid="form-favorite-checkbox" />
        Pin this reminder to the top
      </label>
    </div>
  );
}

export function AlertsStep({ form, setOffsets, today, emailEnabled }) {
  const [custom, setCustomVal] = useState("");
  const nd = form.event_date ? nextOccurrence(form.event_date, form.recurrence, today, form.leap_day_policy) : null;
  const toggle = (o) => setOffsets(form.alert_offsets.includes(o) ? form.alert_offsets.filter((x) => x !== o) : [...form.alert_offsets, o].sort((a, b) => b - a));
  const addCustom = () => {
    const n = parseInt(custom, 10);
    if (Number.isInteger(n) && n >= 0 && n <= 3650 && !form.alert_offsets.includes(n)) toggle(n);
    setCustomVal("");
  };
  return (
    <div data-testid="alerts-step">
      <p className="pm-label">Remind me before the date</p>
      <div className="flex flex-wrap gap-2">
        {[...new Set([...PRESET_OFFSETS, ...form.alert_offsets])].sort((a, b) => b - a).map((o) => (
          <button key={o} type="button" onClick={() => toggle(o)} className="pm-tab" data-active={form.alert_offsets.includes(o)} data-testid={`offset-chip-${o}`}>{o === 0 ? "On the day" : `${o} day${o === 1 ? "" : "s"}`}</button>
        ))}
      </div>
      <div className="flex gap-2 mt-3 max-w-xs">
        <input className="pm-input" type="number" min="0" max="3650" placeholder="Custom days" value={custom} onChange={(e) => setCustomVal(e.target.value)} data-testid="offset-custom-input" />
        <button type="button" className="pm-btn pm-btn-outline" onClick={addCustom} data-testid="offset-custom-add">Add</button>
      </div>
      <p className="pm-label mt-7">Channels</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-xl border border-[var(--pm-border)] p-4 flex gap-3"><Bell className="w-5 h-5 text-[var(--pm-primary)]" /><div><p className="font-semibold text-sm">In-app notifications</p><p className="text-xs text-[var(--pm-muted)]">Always on · Notification Center</p></div></div>
        <div className="rounded-xl border border-[var(--pm-border)] p-4 flex gap-3" data-testid="email-channel-status"><Mail className="w-5 h-5 text-[var(--pm-magenta)]" /><div><p className="font-semibold text-sm">Email</p><p className="text-xs text-[var(--pm-muted)]">{emailEnabled ? "On — sent to your account email" : <>Off · <Link to="/settings?tab=notifications" className="text-[var(--pm-primary)] font-semibold">enable in Settings</Link></>}</p></div></div>
      </div>
      <p className="pm-label mt-7">Upcoming alert schedule</p>
      {!nd ? <p className="text-sm text-[var(--pm-muted)]">Set a date first.</p> : form.alert_offsets.length === 0 ? <p className="text-sm text-[var(--pm-muted)]">No alerts selected — you'll still see the countdown.</p> : (
        <div className="rounded-xl border border-[var(--pm-border)] divide-y divide-[var(--pm-border)]" data-testid="alert-schedule-preview">
          {form.alert_offsets.map((o) => {
            const ad = isoOf(addDays(toDate(nd), -o));
            const past = ad < today;
            return (
              <div key={o} className="flex justify-between items-center px-4 py-2.5 text-sm">
                <span>{o === 0 ? "On the day" : `${o} days before`}</span>
                <span className="flex items-center gap-2"><strong>{fmtDate(ad)}</strong>{past && <StatusPill urgency="archived">In the past — won't be scheduled</StatusPill>}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function FilesStep({ files, setFiles, existing = [], onRemoveExisting }) {
  const [drag, setDrag] = useState(false);
  const [err, setErr] = useState("");
  const add = (list) => {
    const arr = Array.from(list);
    const bad = arr.filter((f) => !ALLOWED_TYPES.includes(f.type) || f.size > 10 * 1024 * 1024);
    setErr(bad.length ? `${bad.map((b) => b.name).join(", ")} skipped — use PDF, JPG, PNG or WebP under 10 MB.` : "");
    setFiles([...files, ...arr.filter((f) => !bad.includes(f))].slice(0, 10));
  };
  return (
    <div data-testid="files-step">
      <label onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
        className={`flex flex-col items-center justify-center text-center border-2 border-dashed rounded-2xl p-10 cursor-pointer transition-colors ${drag ? "border-[var(--pm-primary)] bg-[#F5F2FF]" : "border-[#D9D4EC] hover:bg-[#FAF9FF]"}`}>
        <FileUp className="w-8 h-8 text-[var(--pm-primary)]" />
        <p className="font-semibold mt-3">Drag & drop files, or click to browse</p>
        <p className="text-xs text-[var(--pm-muted)] mt-1">PDF, JPG, PNG or WebP · up to 10 MB each · up to 10 files</p>
        <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="file-input" />
      </label>
      {err && <p className="text-xs text-[var(--pm-danger)] mt-2" role="alert">{err}</p>}
      <p className="flex items-center gap-2 text-xs text-[var(--pm-muted)] mt-3"><Lock className="w-3.5 h-3.5" /> Files are stored privately and only you can open them.</p>
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        {existing.map((a) => (
          <div key={a.id} className="flex items-center gap-3 rounded-xl border border-[var(--pm-border)] p-3"><FileText className="w-5 h-5 text-[var(--pm-primary)]" /><span className="flex-1 min-w-0"><span className="block text-sm font-semibold truncate">{a.original_filename}</span><span className="text-xs text-[var(--pm-muted)]">Saved · {fmtBytes(a.file_size)}</span></span>
            {onRemoveExisting && <button type="button" onClick={() => onRemoveExisting(a)} aria-label="Remove"><X className="w-4 h-4" /></button>}</div>
        ))}
        {files.map((f, i) => (
          <div key={f.name + i} className="flex items-center gap-3 rounded-xl border border-[var(--pm-border)] p-3" data-testid={`pending-file-${i}`}><FileText className="w-5 h-5 text-[var(--pm-magenta)]" /><span className="flex-1 min-w-0"><span className="block text-sm font-semibold truncate">{f.name}</span><span className="text-xs text-[var(--pm-muted)]">Ready to upload · {fmtBytes(f.size)}</span></span>
            <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label="Remove file" data-testid={`remove-pending-file-${i}`}><X className="w-4 h-4" /></button></div>
        ))}
      </div>
    </div>
  );
}

export function ReviewStep({ form, meta, entities, files, today }) {
  const cat = meta.categories.find((c) => c.id === form.category_id);
  const sub = cat?.subcategories.find((s) => s.id === form.subcategory_id);
  const owner = entities.find((e) => e.id === form.owner_id);
  const d = form.event_date ? daysLeft(form.event_date, form.recurrence, today, form.leap_day_policy) : null;
  const rows = [
    ["Title", form.title], ["Category", `${cat?.name} → ${sub?.name}`], ["Owner", owner?.name || "—"],
    ["Date", form.event_date ? `${fmtDate(form.event_date)}${form.date_kind === "instant" && form.event_time ? ` at ${form.event_time} ${form.time_zone || ""}` : ""}` : "—"],
    ["Repeats", RECURRENCE_LABELS[form.recurrence]], ["Alerts", form.alert_offsets.length ? form.alert_offsets.map((o) => (o === 0 ? "on the day" : `${o}d`)).join(", ") : "None"],
    ["Attachments", files.length ? `${files.length} new file${files.length > 1 ? "s" : ""}` : "None"],
    ...Object.entries(form.custom_fields).filter(([, v]) => v !== "" && v != null).map(([k, v]) => [meta.field_defs[k]?.label || k, typeof v === "boolean" ? (v ? "Yes" : "No") : v]),
    ...(form.cost_amount ? [["Estimated cost", `${form.cost_currency || ""} ${form.cost_amount}`]] : []),
    ...(form.notes ? [["Notes", form.notes]] : []),
  ];
  return (
    <div data-testid="review-step">
      {d !== null && <div className="mb-5"><StatusPill urgency={d < 0 ? "overdue" : d <= 30 ? "due_soon" : "upcoming"}>{countdownLabel(d)}</StatusPill></div>}
      <dl className="divide-y divide-[var(--pm-border)] rounded-xl border border-[var(--pm-border)]">
        {rows.map(([k, v]) => <div key={k} className="flex justify-between gap-4 px-4 py-3 text-sm"><dt className="text-[var(--pm-muted)]">{k}</dt><dd className="font-semibold text-right break-words min-w-0">{v}</dd></div>)}
      </dl>
      <p className="flex items-center gap-2 text-xs text-[var(--pm-muted)] mt-4"><Info className="w-3.5 h-3.5" /> This reminder is private to your account.</p>
    </div>
  );
}
